import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Page,
  Card,
  BlockStack,
  Banner,
  Box,
  Text,
  Button,
} from "@shopify/polaris";
import { SaveBar } from "@shopify/app-bridge-react";
import {
  SettingsIcon,
  ProductIcon,
  ThemeEditIcon,
  LayoutColumns3Icon,
  ViewIcon,
  ColorIcon,
} from "@shopify/polaris-icons";
import { fetchTemplate, createTemplate, updateTemplate } from "../../api/templatesApi";
import { useAppTitleBar } from "../../hooks/useAppTitleBar";
import ShippingBarPreview, { PLACEMENT_LABELS } from "../../components/settings/ShippingBarPreview";
import ContentSection from "../../components/settings/ContentSection";
import TemplatesSection from "../../components/settings/TemplatesSection";
import UpsellSection from "../../components/settings/UpsellSection";
import DesignSection from "../../components/settings/DesignSection";
import PlacementTargetingSection from "../../components/settings/PlacementTargetingSection";
import BarSetupSection from "../../components/settings/BarSetupSection";
import EditorShell from "../../components/settings/EditorShell";
import { showAppToast } from "../../utils/showAppToast";
import { useStoreMarketCurrencies } from "../../hooks/useStoreMarketCurrencies";
import { getDefaultBarDesign, normalizeDesignTemplate } from "../../utils/barDesignTemplates";
import { normalizePlacementPages } from "../../utils/placementDefaults";
import { buildInitialBarFromPlacement } from "../../utils/barPresetBuilder";
import { EditorPageSkeleton } from "../../components/PageSkeleton";

const SAVE_BAR_ID = "fsb-bar-editor-save-bar";

const LEFT_NAV = [
  { id: "content", label: "Content", icon: SettingsIcon },
  { id: "templates", label: "Templates", icon: ColorIcon },
  { id: "upsell", label: "Upsell", icon: ProductIcon },
  { id: "design", label: "Design", icon: ThemeEditIcon },
  { id: "placement", label: "Placement", icon: LayoutColumns3Icon },
  { id: "setup", label: "Setup", icon: ViewIcon },
];

const TAB_HINTS = {
  content:
    "Set your free shipping goal and messages. This app shows a progress bar only — add a matching free-shipping rate in Shopify Settings → Shipping and delivery (link below Goal).",
  templates: "Choose a visual template for your bar. Customize colors and layout in the Design tab.",
  upsell: "Add product recommendations to increase order value before shoppers reach free shipping.",
  design: "Customize colors, typography, progress bar, and button styling for your shipping bar.",
  placement: "Choose which pages, countries, and products see this bar. Position is configured in Setup.",
  setup:
    "Quick setup: save your bar, copy the Booster ID, then add the app block in the theme editor. Remember to configure free shipping rates in Shopify admin so checkout matches your goal.",
};

const DEFAULT_CONTENT = {
  goal: { amount: 50, currency: "USD" },
  messages: {
    initial: "Free shipping for orders over {order-value}!",
    progress: "You're only {order-value-progress} away from free shipping!",
    success: "Congratulations! You've got free shipping!",
  },
  translations: [],
};

const DEFAULT_UPSELL = {
  mode: "none",
  products: [],
  cta: { action: "add_to_cart", button_text: "Add to cart" },
};

const DEFAULT_DESIGN = getDefaultBarDesign();

const DEFAULT_PLACEMENT = {
  pages: "all_products",
  product_ids: [],
  selected_products: [],
  collection_ids: [],
  selected_collections: [],
  tags: [],
  excluded_pages: { page_types: [], specific_urls: [], url_keywords: [] },
  geolocation: { mode: "all_world", countries: [] },
  exclude: { mode: "none", product_ids: [], selected_products: [], tags: [] },
};

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function mergeDeep(base, patch) {
  if (!patch || typeof patch !== "object") return deepClone(base);
  const out = Array.isArray(base) ? [...base] : { ...base };
  Object.keys(patch).forEach((k) => {
    const pv = patch[k];
    const bv = out[k];
    if (pv && typeof pv === "object" && !Array.isArray(pv) && bv && typeof bv === "object" && !Array.isArray(bv)) {
      out[k] = mergeDeep(bv, pv);
    } else {
      out[k] = pv;
    }
  });
  return out;
}

function normalizeDesign(raw) {
  const d = mergeDeep(DEFAULT_DESIGN, raw || {});
  const bar = d.bar;
  if (!bar || typeof bar !== "object") return d;

  const pad = bar.padding || {};
  d.card = mergeDeep(d.card, {
    background: bar.background ?? d.card.background,
    background_type: bar.background_type ?? d.card.background_type,
    background_pattern: bar.background_pattern ?? d.card.background_pattern,
    background_image: bar.background_image ?? d.card.background_image,
    corner_radius: bar.border_radius ?? d.card.corner_radius,
    spacing: {
      inside_top: pad.top ?? d.card.spacing?.inside_top,
      inside_bottom: pad.bottom ?? d.card.spacing?.inside_bottom,
      outside_top: d.card.spacing?.outside_top,
      outside_bottom: d.card.spacing?.outside_bottom,
    },
  });
  d.progress_bar = mergeDeep(d.progress_bar, {
    background: bar.progress_track ?? d.progress_bar.background,
    fill: bar.progress_fill ?? d.progress_bar.fill,
  });
  if (bar.text_color && d.typography?.message) {
    d.typography = mergeDeep(d.typography, {
      message: { ...d.typography.message, color: bar.text_color },
    });
  }
  d.layout = mergeDeep(d.layout || { widget_width_percent: 100 }, {});
  return normalizeDesignTemplate(d);
}

function metafieldSyncWarning(metafieldSync) {
  if (!metafieldSync || metafieldSync.synced) return null;
  const detail = metafieldSync.error || metafieldSync.reason || "Unknown error";
  return `Storefront metafield was not updated. Shoppers may see outdated settings until you save again: ${detail}`;
}

export default function BarEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isCreateMode = id === "new";
  const createSeed = location.state || {};

  const [loading, setLoading] = useState(!isCreateMode);
  const [error, setError] = useState(null);
  const [metafieldWarning, setMetafieldWarning] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [leftNav, setLeftNav] = useState("content");

  const [name, setName] = useState("Free shipping bar");
  const [publicId, setPublicId] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [placementType, setPlacementType] = useState("product_page");
  const [content, setContent] = useState(deepClone(DEFAULT_CONTENT));
  const [upsell, setUpsell] = useState(deepClone(DEFAULT_UPSELL));
  const [design, setDesign] = useState(deepClone(DEFAULT_DESIGN));
  const [placement, setPlacement] = useState(deepClone(DEFAULT_PLACEMENT));

  const { currencyOptions, loading: currencyLoading, primaryCurrency } = useStoreMarketCurrencies();

  const savedRef = useRef(null);
  const hasSavedOnceRef = useRef(false);
  const initialSnapshotRef = useRef(null);
  const presetKeyRef = useRef(createSeed.preset_key || "product_inline_minimal");

  const snapshot = useMemo(
    () => ({
      name,
      content: deepClone(content),
      upsell: deepClone(upsell),
      design: deepClone(design),
      placement: deepClone(placement),
    }),
    [name, content, upsell, design, placement],
  );

  // Compute on every render (not useMemo): after save we update savedRef without
  // changing snapshot, so a memoized value would stay stuck on "dirty".
  const saveBarOpen = (() => {
    if (loading) return false;
    if (isCreateMode && !hasSavedOnceRef.current) return true;
    if (savedRef.current == null) return false;
    return JSON.stringify(snapshot) !== JSON.stringify(savedRef.current);
  })();

  const previewMode = leftNav === "content" ? "content" : "progress";

  useEffect(() => {
    if (isCreateMode) {
      if (!createSeed.placement_type) {
        navigate("/onboarding/choose", { replace: true });
        return;
      }
      const initial = buildInitialBarFromPlacement(createSeed.placement_type, createSeed.preset_key);
      presetKeyRef.current = initial.presetKey;
      const baseline = {
        name: initial.name,
        content: deepClone(initial.content),
        upsell: deepClone(initial.upsell),
        design: deepClone(initial.design),
        placement: deepClone(initial.placement),
      };
      initialSnapshotRef.current = baseline;
      setName(initial.name);
      setPlacementType(initial.placementType);
      setContent(initial.content);
      setUpsell(initial.upsell);
      setDesign(initial.design);
      setPlacement(initial.placement);
      setIsPublished(false);
      setPublicId("");
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchTemplate(id);
        const t = res?.data?.template;
        if (!t) throw new Error("Bar not found");
        if (cancelled) return;
        const loaded = {
          name: t.name || "Free shipping bar",
          content: mergeDeep(DEFAULT_CONTENT, t.content || {}),
          upsell: mergeDeep(DEFAULT_UPSELL, t.upsell || {}),
          design: normalizeDesign(t.design || {}),
          placement: mergeDeep(DEFAULT_PLACEMENT, t.placement || {}),
        };
        loaded.placement.pages = normalizePlacementPages(loaded.placement.pages, t.placement_type);
        setName(loaded.name);
        setPublicId(t.public_id || "");
        setIsPublished(!!t.is_published);
        setPlacementType(t.placement_type || "product_page");
        setContent(loaded.content);
        setUpsell(loaded.upsell);
        setDesign(loaded.design);
        setPlacement(loaded.placement);
        savedRef.current = deepClone(loaded);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load bar");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isCreateMode, createSeed.placement_type, createSeed.preset_key, navigate]);

  const onSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setMetafieldWarning(null);
    try {
      if (isCreateMode) {
        const res = await createTemplate({
          placement_type: placementType,
          preset_key: presetKeyRef.current,
          name,
          content,
          upsell,
          design,
          placement,
        });
        const newId = res?.data?.template?.id;
        if (!newId) throw new Error("Could not create bar");
        const warning = metafieldSyncWarning(res?.data?.metafieldSync);
        if (warning) {
          setMetafieldWarning(warning);
          showAppToast("Bar saved as draft, but storefront sync failed", { isError: true });
        } else {
          showAppToast("Bar saved as draft");
        }
        hasSavedOnceRef.current = true;
        navigate(`/settings/bars/${newId}`, { replace: true });
        return;
      }

      const res = await updateTemplate(id, { name, content, upsell, design, placement });
      savedRef.current = deepClone(snapshot);
      const warning = metafieldSyncWarning(res?.data?.metafieldSync);
      if (warning) {
        setMetafieldWarning(warning);
        showAppToast("Changes saved, but storefront sync failed", { isError: true });
      } else {
        showAppToast("Changes saved");
      }
    } catch (e) {
      setError(e?.message || "Save failed");
      showAppToast(e?.message || "Save failed", { isError: true });
    } finally {
      setSaving(false);
    }
  }, [id, isCreateMode, placementType, name, content, upsell, design, placement, snapshot, navigate]);

  const onTogglePublish = useCallback(
    async (publish) => {
      if (isCreateMode) return;
      setPublishing(true);
      setError(null);
      setMetafieldWarning(null);
      try {
        const res = await updateTemplate(id, {
          name,
          content,
          upsell,
          design,
          placement,
          is_published: publish,
        });
        savedRef.current = deepClone(snapshot);
        const updated = res?.data?.template;
        setIsPublished(!!(updated?.is_published ?? publish));
        if (updated?.public_id) setPublicId(updated.public_id);
        const warning = metafieldSyncWarning(res?.data?.metafieldSync);
        if (warning) {
          setMetafieldWarning(warning);
          showAppToast(
            publish
              ? "Bar published in the app, but storefront sync failed"
              : "Bar moved to draft, but storefront sync failed",
            { isError: true },
          );
        } else {
          showAppToast(
            publish
              ? "Bar published — now live on your storefront"
              : "Bar moved to draft — hidden from storefront",
          );
        }
      } catch (e) {
        setError(e?.message || "Status update failed");
        showAppToast(e?.message || "Status update failed", { isError: true });
      } finally {
        setPublishing(false);
      }
    },
    [id, isCreateMode, name, content, upsell, design, placement, snapshot],
  );

  const onDiscard = useCallback(() => {
    if (isCreateMode && !hasSavedOnceRef.current) {
      const initial = initialSnapshotRef.current;
      if (!initial) return;
      setName(initial.name);
      setContent(deepClone(initial.content));
      setUpsell(deepClone(initial.upsell));
      setDesign(deepClone(initial.design));
      setPlacement(deepClone(initial.placement));
      return;
    }
    const saved = savedRef.current;
    if (saved) {
      setName(saved.name);
      setContent(deepClone(saved.content));
      setUpsell(deepClone(saved.upsell));
      setDesign(deepClone(saved.design));
      setPlacement(deepClone(saved.placement));
    }
  }, [isCreateMode]);

  useAppTitleBar({
    title: isCreateMode ? "Create shipping bar" : "Edit shipping bar",
    breadcrumb: { label: "Bars", onClick: () => navigate("/settings/bars") },
  });

  const patchContent = useCallback((patch) => setContent((prev) => mergeDeep(prev, patch)), []);
  const patchUpsell = useCallback((patch) => setUpsell((prev) => mergeDeep(prev, patch)), []);
  const patchDesign = useCallback((patch) => setDesign((prev) => mergeDeep(prev, patch)), []);
  const patchPlacement = useCallback((patch) => setPlacement((prev) => mergeDeep(prev, patch)), []);

  const publishHeaderAction = (
    <Button
      variant={isPublished ? "secondary" : "primary"}
      loading={publishing}
      disabled={isCreateMode || saving || publishing}
      onClick={() => void onTogglePublish(!isPublished)}
    >
      {isPublished ? "Draft" : "Publish"}
    </Button>
  );

  if (loading) {
    return <EditorPageSkeleton />;
  }

  return (
    <>
      <SaveBar id={SAVE_BAR_ID} open={saveBarOpen} discardConfirmation>
        <button
          type="button"
          variant="primary"
          loading={saving ? "" : undefined}
          onClick={(e) => {
            e.preventDefault();
            void onSave();
          }}
          disabled={saving || !saveBarOpen}
        >
          Save
        </button>
        <button type="button" onClick={onDiscard} disabled={saving || !saveBarOpen}>
          Discard
        </button>
      </SaveBar>

      <Page fullWidth titleHidden>
        <BlockStack gap="500">
          {error ? (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          ) : null}
          {metafieldWarning ? (
            <Banner tone="warning" onDismiss={() => setMetafieldWarning(null)}>
              {metafieldWarning}
            </Banner>
          ) : null}

          <EditorShell
            nav={LEFT_NAV}
            activeId={leftNav}
            onNavChange={setLeftNav}
            hint={TAB_HINTS[leftNav]}
            headerAction={publishHeaderAction}
          >
            {leftNav === "setup" ? (
              <div className="fsb-editor-tab-content">
                <BarSetupSection
                  publicId={publicId}
                  placementType={placementType}
                  placement={placement}
                  onPatchPlacement={patchPlacement}
                />
              </div>
            ) : (
              <div className="fsb-editor-split fsb-editor-tab-content">
                <div className="fsb-editor-split__form">
                  <Card padding="0">
                    <Box padding="400">
                      <BlockStack gap="300">
                        {leftNav === "content" ? (
                          <ContentSection
                            name={name}
                            content={content}
                            design={design}
                            currencyOptions={currencyOptions}
                            currencyLoading={currencyLoading}
                            primaryCurrency={primaryCurrency}
                            onPatchName={setName}
                            onPatch={patchContent}
                            onPatchDesign={patchDesign}
                          />
                        ) : null}
                        {leftNav === "templates" ? (
                          <TemplatesSection design={design} onApplyTemplate={setDesign} />
                        ) : null}
                        {leftNav === "upsell" ? (
                          <UpsellSection upsell={upsell} onPatch={patchUpsell} />
                        ) : null}
                        {leftNav === "design" ? (
                          <DesignSection
                            design={design}
                            placementType={placementType}
                            onPatch={patchDesign}
                          />
                        ) : null}
                        {leftNav === "placement" ? (
                          <PlacementTargetingSection
                            placement={placement}
                            placementType={placementType}
                            onPatch={patchPlacement}
                          />
                        ) : null}
                      </BlockStack>
                    </Box>
                  </Card>
                </div>

                <div className="fsb-editor-split__preview fsb-editor-preview-sticky">
                  <Card padding="0">
                    <Box padding="400">
                      <BlockStack gap="300">
                        <Text as="h2" variant="headingSm">
                          {PLACEMENT_LABELS[placementType] || "Live preview"}
                        </Text>
                        <ShippingBarPreview
                          content={content}
                          design={design}
                          upsell={upsell}
                          placementType={placementType}
                          previewMode={previewMode}
                        />
                      </BlockStack>
                    </Box>
                  </Card>
                </div>
              </div>
            )}
          </EditorShell>
        </BlockStack>
      </Page>
    </>
  );
}
